import {
  LayoutGridContainer,
  LayoutGridItem,
  DatePickerSingle,
  useMediumFromMediaQuery,
} from '@madpaws/design-system';
import { useFormikContext } from 'formik';
import React from 'react';

import { trackEvent } from '~/components/analytics/analytics';
import { UPDATE_FILTER_REPEAT_WEEKLY_CALENDAR_EVENT_NAME } from '~/components/analytics/constants';

import { WeekDaysSelector } from './WeekDaysSelector';
import { SEARCH_FILTERS_FORM_KEYS as KEYS } from '../../constants';

import type { DateFormat } from '@madpaws/design-system';
import type { ColumnPlacement } from '@madpaws/design-system/dist/components/LayoutGrid/types';
import type { ReactElement } from 'react';
import type { SearchFilters } from '~/common/types/search';

type RwbDatesSelectorProps = {
  dateFormat?: DateFormat;
  disabledDates?: string[];
  isCalendarIconHidden?: boolean;
  isCheckboxLegendVisuallyHidden?: boolean;
  isFromExpandedSearchBar?: boolean;
  isLabelVisuallyHidden?: boolean;
  name?: string;
  onDayPickerShow?: () => void;
  rwbStartingFromPlaceholder?: string;
  rwbplaceholder?: string;
  weekDaySelectorLabel?: string;
};

export const RwbDatesSelector = ({
  dateFormat = 'short',
  disabledDates = [],
  isCalendarIconHidden = true,
  isCheckboxLegendVisuallyHidden,
  isLabelVisuallyHidden,
  name,
  rwbStartingFromPlaceholder = 'Start week',
  rwbplaceholder = 'Add days',
  weekDaySelectorLabel,
  onDayPickerShow,
  isFromExpandedSearchBar,
}: RwbDatesSelectorProps): ReactElement => {
  const {
    values: {
      chronology: { rwbStartDate },
    },
    setFieldValue,
  } = useFormikContext<SearchFilters>();

  const isMediumFromViewport = useMediumFromMediaQuery();

  const onDateSelection = (date: string): void => {
    setFieldValue(KEYS.rwbStartDate, date);
    trackEvent(UPDATE_FILTER_REPEAT_WEEKLY_CALENDAR_EVENT_NAME);
  };

  const weekDaySelectorGridItemPlacement: ColumnPlacement =
    isFromExpandedSearchBar && !isMediumFromViewport
      ? [1, 13]
      : isFromExpandedSearchBar
      ? [1, 9]
      : [1, 7];

  const datePickerSingleGridItemPlacement: ColumnPlacement =
    isFromExpandedSearchBar && !isMediumFromViewport
      ? [1, 13]
      : isFromExpandedSearchBar
      ? [9, 13]
      : [7, 13];

  return (
    <LayoutGridContainer
      columnGap={{ small: 'default', medium: 'default', large: 'small2x' }}
      rowGap={isFromExpandedSearchBar && !isMediumFromViewport ? 'small1x' : 'none'}
    >
      <LayoutGridItem placement={weekDaySelectorGridItemPlacement}>
        <WeekDaysSelector
          isCalendarIconHidden={isCalendarIconHidden}
          isFromExpandedSearchBar={isFromExpandedSearchBar}
          isLabelVisuallyHidden={isLabelVisuallyHidden}
          isLegendVisuallyHidden={isCheckboxLegendVisuallyHidden}
          label={weekDaySelectorLabel}
          name={name}
          placeholder={isFromExpandedSearchBar ? 'Days' : rwbplaceholder}
        />
      </LayoutGridItem>
      <LayoutGridItem placement={datePickerSingleGridItemPlacement}>
        <DatePickerSingle
          dateFormat={dateFormat}
          daysOfWeek={[1]}
          disabledDates={disabledDates}
          hasTighterSpacing
          isCalendarIconHidden={isCalendarIconHidden}
          isLabelVisuallyHidden={isLabelVisuallyHidden}
          isRightAligned
          label={isFromExpandedSearchBar ? 'Starting from' : 'Start week'}
          name={name ? name : KEYS.rwbStartDate}
          onDateSelection={onDateSelection}
          onDayPickerShow={onDayPickerShow}
          placeholder={rwbStartingFromPlaceholder}
          value={rwbStartDate}
        />
      </LayoutGridItem>
    </LayoutGridContainer>
  );
};
